import {
  RectangleStackIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import type { ReactNode } from 'react';
import type { FormSchemaV2, SectionKind } from '../../lib/agentShared/formSchema';
import {
  ContextHeader,
  DataList,
  Stack,
  Surface,
  ToolbarActions,
} from '../ui';

export type PreviewViewport = 'web' | 'tablet' | 'mobile';

const SECTION_ICON: Record<SectionKind, ComponentType<SVGProps<SVGSVGElement>>> = {
  section: Squares2X2Icon,
  page: RectangleStackIcon,
  table: TableCellsIcon,
};

const navigationActionClass =
  'min-w-0 [&>button]:min-w-0 [&>button]:max-w-full [&>button]:overflow-hidden [&>button]:text-ellipsis';

export function TemplatePreviewOutline({
  schema,
  activeSectionId,
  onSelectSection,
}: {
  schema: FormSchemaV2;
  activeSectionId: string | null;
  onSelectSection: (sectionId: string) => void;
}): JSX.Element {
  return (
    <Surface as="section" variant="bare" className="min-h-full" data-testid="template-preview-outline">
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader
          title="Preview sections"
          headingLevel={2}
          action={<span className="text-caption text-muted-foreground">{schema.sections.length}</span>}
        />
      </Surface>
      <Stack as="nav" aria-label="Preview sections" gap={1} className="p-3">
        {schema.sections.map((section, index) => {
          const SectionIcon = SECTION_ICON[section.kind ?? 'section'];
          const selected = section.id === activeSectionId;
          return (
            <ToolbarActions
              key={section.id}
              size="sm"
              maxVisible={1}
              className={navigationActionClass}
              actions={[{
                id: section.id,
                label: section.name || `Section ${index + 1}`,
                ariaLabel: `Preview ${section.name || `section ${index + 1}`}`,
                icon: <SectionIcon className="h-4 w-4" />,
                variant: selected ? 'secondary' : 'ghost',
                pressed: selected,
                onClick: () => onSelectSection(section.id),
                'data-testid': `preview-section-${section.id}`,
              }]}
            />
          );
        })}
      </Stack>
    </Surface>
  );
}

export function TemplatePreviewInspector({
  schema,
}: {
  schema: FormSchemaV2;
}): JSX.Element {
  const fields = schema.sections.flatMap((section) => section.fields);
  const required = fields.filter((field) => field.required).length;
  const conditional = fields.filter((field) => (field.visibleWhen?.length ?? 0) > 0).length;

  return (
    <Surface as="section" variant="bare" className="min-h-full" data-testid="template-preview-inspector">
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader title="Preview options" headingLevel={2} />
      </Surface>
      <Stack gap={5} className="min-w-0 p-4">
        <DataList
          density="compact"
          items={[
            { label: 'Sections', value: schema.sections.length },
            { label: 'Questions', value: fields.length },
            { label: 'Required', value: required },
            { label: 'Conditional', value: conditional },
          ]}
        />
        <p className="break-words text-caption leading-relaxed text-muted-foreground">
          This is a live inspection preview. Responses are temporary and do not change the template.
        </p>
      </Stack>
    </Surface>
  );
}

const PREVIEW_VIEWPORT_CLASS: Record<PreviewViewport, string> = {
  web: 'max-w-5xl',
  tablet: 'max-w-2xl',
  mobile: 'max-w-sm',
};

/**
 * Width-governed live preview surface. This deliberately does not compose
 * `Container`: its semantic width token is an inline max-width and would win
 * over the selectable viewport widths in the cascade.
 */
export function TemplatePreviewFrame({
  viewport,
  children,
}: {
  viewport: PreviewViewport;
  children: ReactNode;
}): JSX.Element {
  return (
    <Stack
      gap={3}
      className={`mx-auto w-full ${PREVIEW_VIEWPORT_CLASS[viewport]}`}
      data-testid="template-preview-canvas"
    >
      {children}
    </Stack>
  );
}

// `TemplateReportTools` (the Report tab's old bare Style/Content tool
// switcher) is RETIRED as of story 4-113 — replaced by
// `ReportStudioOutline`/`ReportStudioInspector`/`ReportStudioViewportTools`
// (`components/templates/ReportStudioPanels.tsx`), a real section-addressable
// outline driving a selection-scoped inspector instead of a permanently
// mounted tool switcher. See that file's own header comment for the full
// rationale and this story's own scope boundary.
